import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconAlertCircle, IconTrash } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import {
  isPinRequiredResponse,
  pinUnlockHeaders,
} from "../../utils/pinUnlockSession";
import { ScaleTable, normalizeScaleLines } from "./payEditorPieces";

const money = value =>
  value == null
    ? ""
    : `$${Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })}`;

// Sentinel option value meaning "all of this rep's direct recruits".
const ALL_RECRUITS = "__all_recruits__";

// The Override tab. Every override this rep earns is a "peer override" behind
// the scenes (the rate-stack engine's per-person override), so there is no
// separate section — it all lives here. The defaults match how overrides are
// normally granted: a minimum-margin-guarantee funded by the next level up.
//
//  - "Apply to every direct recruit" (default on) targets everyone the rep has
//    recruited this season.
//  - The rep search adds specific target reps on top of (or instead of) that.
//
// recipient = this rep (earns), target = each selected rep (production),
// grantor = the rep's manager / recruiter (funds it from their margin).
function OverrideTab({ repId, repName, sellingYearId, onPinRequired }) {
  const { getVerifiedJWT } = useAuth();

  const [people, setPeople] = useState([]);
  const [repUserId, setRepUserId] = useState(null);
  const [nextLevelUp, setNextLevelUp] = useState(null); // {user_id, name}
  const [grantorUserId, setGrantorUserId] = useState(null);
  const [recruitRepIds, setRecruitRepIds] = useState([]);
  const [existing, setExisting] = useState([]);

  const [tierLines, setTierLines] = useState([
    { revenue_amount: 0, percentage: 0 },
  ]);
  // Target selection: "Every direct recruit" (the sentinel) is selected by
  // default; individual reps can be added or picked instead.
  const [selectedTargets, setSelectedTargets] = useState([ALL_RECRUITS]);

  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);

  const authedFetch = useCallback(
    async (path, options = {}) => {
      const jwt = await getVerifiedJWT();
      if (!jwt) throw new Error("Authentication failed");
      return fetch(getApiEndpoint(path), {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          ...pinUnlockHeaders(),
          ...(options.headers || {}),
        },
      });
    },
    [getVerifiedJWT]
  );

  const personName = p =>
    `${p.first_name || ""} ${p.last_name || ""}`.trim() ||
    p.email ||
    `Rep ${p.rep_id ?? p.id}`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const yearQ = sellingYearId ? `?selling_year_id=${sellingYearId}` : "";
      const [overviewRes, repRes, listRes] = await Promise.all([
        authedFetch(`/v1/ps/overview${yearQ}`),
        authedFetch(`/v1/rep/details/${repId}`),
        authedFetch(`/v1/ps/peer-overrides?involving_rep_id=${repId}`),
      ]);

      const peopleList = overviewRes.ok
        ? (await overviewRes.json())?.data?.recruited_by_options || []
        : [];
      setPeople(Array.isArray(peopleList) ? peopleList : []);

      // The rep's own internal user id (recipient) from the people list.
      const self = peopleList.find(p => String(p.rep_id) === String(repId));
      const resolvedRepUserId = self ? self.id : null;
      setRepUserId(resolvedRepUserId);

      // The next level up: the rep's manager, else their recruiter. Used as
      // the default guarantor (who gives the override).
      if (repRes.ok) {
        const rep = (await repRes.json())?.data ?? {};
        let up = null;
        if (rep.manager_id) {
          up = { user_id: rep.manager_id, name: rep.manager_name };
        } else if (rep.recruited_by_id) {
          up = { user_id: rep.recruited_by_id, name: rep.recruited_by_name };
        }
        setNextLevelUp(up);
        // Default the guarantor to the next level up, without clobbering a
        // choice the user already made this session.
        if (up?.user_id != null) {
          setGrantorUserId(prev => prev ?? String(up.user_id));
        }
      }

      if (listRes.ok) {
        const rows = (await listRes.json())?.data || [];
        // Only the overrides this rep earns (recipient), not ones where they
        // are the target of someone else's override.
        setExisting(
          rows.filter(r => String(r.recipient?.rep_id) === String(repId))
        );
      } else {
        const body = await listRes.json().catch(() => ({}));
        if (isPinRequiredResponse(listRes, body)) {
          onPinRequired?.();
          return;
        }
      }

      // The rep's direct recruits (targets for the blanket toggle), mapped from
      // user ids to rep ids via the people list.
      if (resolvedRepUserId) {
        const pgRes = await authedFetch(
          `/v1/ps/peer-overrides/party-groups?user_id=${resolvedRepUserId}${
            sellingYearId ? `&selling_year_id=${sellingYearId}` : ""
          }`
        );
        if (pgRes.ok) {
          const groups = (await pgRes.json())?.data ?? {};
          const recruitUserIds = new Set(
            (groups.recruited_user_ids || []).map(String)
          );
          const repIds = peopleList
            .filter(p => recruitUserIds.has(String(p.id)) && p.rep_id != null)
            .map(p => p.rep_id);
          setRecruitRepIds(repIds);
        }
      }
    } catch (err) {
      setError(err.message || "Failed to load overrides");
    } finally {
      setLoading(false);
    }
  }, [authedFetch, repId, sellingYearId, onPinRequired]);

  useEffect(() => {
    load();
  }, [load]);

  // Reps already covered by an existing override (by target rep_id).
  const coveredRepIds = useMemo(
    () => new Set(existing.map(o => String(o.target?.rep_id))),
    [existing]
  );

  // Search options: everyone with a rep_id, excluding the rep themselves and
  // anyone already covered.
  const repOptions = useMemo(
    () =>
      people
        .filter(
          p =>
            p.rep_id != null &&
            String(p.rep_id) !== String(repId) &&
            !coveredRepIds.has(String(p.rep_id))
        )
        .map(p => ({ value: String(p.rep_id), label: personName(p) })),
    [people, repId, coveredRepIds]
  );

  // The target picker: "Every direct recruit" first, then individual reps.
  const targetOptions = useMemo(
    () => [
      {
        value: ALL_RECRUITS,
        label: `Every direct recruit${
          recruitRepIds.length ? ` (${recruitRepIds.length})` : ""
        }`,
      },
      ...repOptions,
    ],
    [repOptions, recruitRepIds]
  );

  // Guarantor options (by internal user id): everyone, plus the next level up
  // in case they aren't in the people list (e.g. an upline outside the caller's
  // own downline).
  const guarantorOptions = useMemo(() => {
    const opts = people.map(p => ({
      value: String(p.id),
      label: personName(p),
    }));
    if (
      nextLevelUp?.user_id != null &&
      !opts.some(o => o.value === String(nextLevelUp.user_id))
    ) {
      opts.unshift({
        value: String(nextLevelUp.user_id),
        label: nextLevelUp.name || "Next level up",
      });
    }
    return opts;
  }, [people, nextLevelUp]);

  const hasTiers = tierLines.some(l => Number(l.percentage) > 0);

  const apply = async () => {
    setError(null);
    if (!hasTiers) {
      setError("Set at least one override tier (revenue and percentage).");
      return;
    }

    if (!repUserId) {
      setError("Couldn't resolve this rep's account. Try reloading.");
      return;
    }

    // "Every direct recruit" becomes a live blanket override — it applies to
    // whoever the rep recruits (now and later), so it can be added even with no
    // recruits yet. Any hand-picked reps are added as concrete overrides too.
    const includeAllRecruits = selectedTargets.includes(ALL_RECRUITS);
    const specificRepIds = selectedTargets
      .filter(v => v !== ALL_RECRUITS)
      .map(Number)
      .filter(Boolean)
      .filter(id => !coveredRepIds.has(String(id)));

    if (!includeAllRecruits && !specificRepIds.length) {
      setError('Choose a rep, or select "Every direct recruit".');
      return;
    }

    const baseBody = {
      recipient_user_id: Number(repUserId),
      // A minimum-margin guarantee, funded by the chosen guarantor.
      condition_type: "MARGIN_GUARANTEE",
      scale_lines: normalizeScaleLines(tierLines),
    };
    const grantor = grantorUserId ?? nextLevelUp?.user_id;
    if (grantor != null) baseBody.grantor_user_id = Number(grantor);
    if (sellingYearId) baseBody.selling_year_id = Number(sellingYearId);

    // One blanket override per rep at most; add a concrete batch for any
    // specific reps picked alongside it.
    const hasBlanket = existing.some(o => o.target_all_recruits);
    const bodies = [];
    if (includeAllRecruits && !hasBlanket) {
      bodies.push({ ...baseBody, target_all_recruits: true });
    }
    if (specificRepIds.length) {
      bodies.push({ ...baseBody, target_rep_ids: specificRepIds });
    }
    if (!bodies.length) {
      setError("This rep already has an every-direct-recruit override.");
      return;
    }

    try {
      setApplying(true);
      for (const body of bodies) {
        const res = await authedFetch("/v1/ps/peer-overrides", {
          method: "POST",
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          if (isPinRequiredResponse(res, errBody)) {
            onPinRequired?.();
            return;
          }
          throw new Error(errBody.message || `HTTP ${res.status}`);
        }
      }
      setSelectedTargets([ALL_RECRUITS]);
      await load();
    } catch (err) {
      setError(err.message || "Failed to apply the override");
    } finally {
      setApplying(false);
    }
  };

  const remove = async row => {
    try {
      setDeletingId(row.id);
      const res = await authedFetch(`/v1/ps/peer-overrides/${row.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        if (isPinRequiredResponse(res, body)) {
          onPinRequired?.();
          return;
        }
        throw new Error(body.message || `HTTP ${res.status}`);
      }
      await load();
    } catch (err) {
      setError(err.message || "Failed to remove the override");
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Center py="lg">
        <Loader />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          {error}
        </Alert>
      )}

      <ScaleTable
        title="Override Scale"
        col2Label="Override %"
        lines={tierLines}
        onChange={setTierLines}
      />

      <Select
        label="Guarantor"
        description="Who gives (funds) this override. Defaults to the next level up."
        placeholder="Select guarantor"
        data={guarantorOptions}
        value={grantorUserId}
        onChange={setGrantorUserId}
        searchable
      />

      <MultiSelect
        label="Apply to"
        placeholder="Search a rep's name"
        data={targetOptions}
        value={selectedTargets}
        onChange={setSelectedTargets}
        searchable
        clearable
        description="Every direct recruit, or pick specific reps."
      />

      <Group justify="flex-end">
        <Button color="orange" onClick={apply} loading={applying}>
          Apply override
        </Button>
      </Group>

      {existing.length > 0 && (
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Current overrides
          </Text>
          {existing.map(o => (
            <Card key={o.id} withBorder radius="md" p="sm">
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Stack gap={4} style={{ flex: 1 }}>
                  <Group gap="xs">
                    <Text fw={600} size="sm">
                      On{" "}
                      {o.target_all_recruits
                        ? "Every direct recruit"
                        : o.target?.name || "—"}
                    </Text>
                    <Badge color="gray" variant="light">
                      {o.condition_type === "MARGIN_GUARANTEE"
                        ? "Min margin guarantee"
                        : o.condition_type}
                    </Badge>
                  </Group>
                  <Text size="xs" c="dimmed">
                    Funded by {o.grantor?.name || "the next level up"}
                  </Text>
                  <Group gap={6} mt={2}>
                    {(o.scale_lines || []).map(l => (
                      <Badge key={l.id} variant="light" color="orange">
                        {money(l.revenue_amount)} → {Number(l.percentage)}%
                      </Badge>
                    ))}
                  </Group>
                </Stack>
                <Tooltip label="Remove">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    loading={deletingId === o.id}
                    onClick={() => remove(o)}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            </Card>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

export default OverrideTab;
